import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Radio } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { useTicketApi } from '../lib/ticketApi';

const EVERY = 15;
const FOOTBALL = new Set(['nfl', 'cfb']);
const hex = (c, fb) => (c ? `#${c}` : fb);
// ESPN yardLine is measured from the HOME goal line; the strip runs away end zone (left) → home end zone (right).
const pos = (yl) => `${8 + (100 - Math.max(0, Math.min(100, yl))) * 0.84}%`;

// Only a ticket whose legs are all from ONE football game gets the game tracker.
export const singleGame = (ticket) => {
  const legs = ticket.legs || [];
  const ids = new Set(legs.map((l) => l.espn_event_id).filter(Boolean));
  if (ids.size !== 1 || legs.some((l) => !l.espn_event_id) || !legs.every((l) => FOOTBALL.has(l.league))) return null;
  return { league: legs[0].league, eventId: [...ids][0], leg: legs[0] };
};

const useGame = (league, eventId, on) => {
  const api = useTicketApi();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [left, setLeft] = useState(EVERY);
  useEffect(() => {
    if (!on) return undefined;
    let alive = true;
    const get = () => axios.get(`${api.base}/live-field/${league}/${eventId}`, { headers: api.headers })
      .then((r) => { if (alive) { setD(r.data); setErr(''); setLeft(EVERY); } })
      .catch((e) => alive && setErr(e.response?.data?.detail || e.message));
    get();
    const poll = setInterval(get, EVERY * 1000);
    const tick = setInterval(() => setLeft((n) => Math.max(0, n - 1)), 1000);
    return () => { alive = false; clearInterval(poll); clearInterval(tick); };
  }, [league, eventId, on, api]);
  return { d, err, left };
};

const Timeouts = ({ n }) => (
  <span className="flex gap-1">{[0, 1, 2].map((i) => <span key={i} className={`w-2 h-2 rounded-full ${i < (n ?? 0) ? 'bg-amber-400' : 'bg-zinc-700'}`} />)}</span>
);

const Strip = ({ d, big }) => {
  const away = d.teams.find((t) => t.side === 'away') || {};
  const home = d.teams.find((t) => t.side === 'home') || {};
  const s = d.situation || {};
  const has = d.state === 'in' && s.possession && s.yard_line != null;
  const offense = s.possession === 'home' ? home : away;
  const dir = s.possession === 'home' ? -1 : 1; // home drives left, away drives right
  const firstLine = has && s.distance && s.short && !/goal/i.test(s.short) ? (s.possession === 'home' ? s.yard_line + s.distance : s.yard_line - s.distance) : null;
  return (
    <div className={`relative ${big ? 'pt-12' : 'pt-7'}`} data-testid="tracker-strip">
      <div className={`relative ${big ? 'h-9' : 'h-5'} rounded-sm overflow-hidden bg-zinc-200`}>
        <div className="absolute inset-y-0 left-0 w-[8%]" style={{ background: hex(away.color, '#1e3a8a') }} />
        <div className="absolute inset-y-0 right-0 w-[8%]" style={{ background: hex(home.color, '#0e7490') }} />
        {[...Array(9)].map((_, i) => <div key={i} className="absolute inset-y-0 w-px bg-white" style={{ left: `${8 + (i + 1) * 8.4}%` }} />)}
        {firstLine != null && <div className="absolute inset-y-0 w-[2px] bg-amber-400" style={{ left: pos(firstLine) }} data-testid="tracker-first-down" />}
        {has && <div className="absolute inset-y-0 w-[2px] bg-zinc-500" style={{ left: pos(s.yard_line) }} />}
      </div>
      {has && (
        <div className="absolute top-0 -translate-x-1/2 flex flex-col items-center" style={{ left: pos(s.yard_line) }} data-testid="tracker-ball">
          <div className={`flex items-center gap-1 rounded-full bg-zinc-100 ${big ? 'px-2.5 py-1' : 'px-1.5 py-0.5'} shadow`}>
            {dir < 0 && <span className={`font-black text-zinc-500 ${big ? 'text-sm' : 'text-[9px]'}`}>‹‹‹</span>}
            {offense.logo && <img src={offense.logo} alt="" className={big ? 'w-6 h-6' : 'w-3.5 h-3.5'} />}
            {dir > 0 && <span className={`font-black text-zinc-500 ${big ? 'text-sm' : 'text-[9px]'}`}>›››</span>}
          </div>
          <div className={`${big ? 'mt-2 w-4 h-2.5' : 'mt-1 w-2.5 h-1.5'} rounded-full bg-amber-900 border border-white`} />
        </div>
      )}
      <div className={`flex justify-between text-zinc-400 ${big ? 'text-xs mt-1' : 'text-[8px]'}`}>
        <span>{away.abbr}</span><span>20</span><span>50</span><span>20</span><span>{home.abbr}</span>
      </div>
    </div>
  );
};

const Header = ({ d }) => {
  const s = d.situation || {};
  const side = (t, right) => (
    <div className={`flex-1 min-w-0 ${right ? 'text-right' : ''}`}>
      <div className={`flex items-center gap-2 ${right ? 'flex-row-reverse' : ''}`}>
        {t.logo && <img src={t.logo} alt="" className="w-10 h-10 object-contain" />}
        <span className="font-mono text-4xl font-black text-white" data-testid={`tracker-score-${t.side}`}>{t.score}</span>
      </div>
      <p className="text-sm font-semibold text-white mt-1 truncate">{t.short || t.abbr}</p>
      {t.record && <p className="text-[11px] text-zinc-500">({t.record})</p>}
      <div className={`mt-1 flex items-center gap-1.5 ${right ? 'justify-end' : ''}`}>
        {right && s.possession === t.side && <span className="text-sm">🏈</span>}
        <Timeouts n={t.side === 'home' ? s.home_timeouts : s.away_timeouts} />
        {!right && s.possession === t.side && <span className="text-sm">🏈</span>}
      </div>
    </div>
  );
  const away = d.teams.find((t) => t.side === 'away') || {};
  const home = d.teams.find((t) => t.side === 'home') || {};
  return (
    <div>
      <div className="flex items-start gap-2">
        {side(away, false)}
        <p className="pt-3 shrink-0 font-mono text-sm font-bold text-emerald-400" data-testid="tracker-clock">
          {d.state === 'in' ? `Q${d.period} · ${d.clock}` : d.detail}
        </p>
        {side(home, true)}
      </div>
      {s.text && d.state === 'in' && <p className="text-center text-sm text-zinc-200 mt-1" data-testid="tracker-down">{s.text}{s.red_zone && <span className="ml-1.5 rounded bg-red-600 px-1 text-[9px] font-black text-white align-middle">RED ZONE</span>}</p>}
    </div>
  );
};

// Compact strip at the top of a single-game ticket; tap for the full live tracker.
export default function GameTracker({ league, eventId, leg }) {
  const [open, setOpen] = useState(false);
  const mini = useGame(league, eventId, !open && !!leg.period_num);
  const full = useGame(league, eventId, open);
  const d = open ? full.d : mini.d;
  const offense = d && d.teams.find((t) => t.side === d.situation?.possession);
  const plays = d && d.drive?.team === offense?.abbr ? d.drive.plays : [];
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`tracker-open-${eventId}`}
        className="w-full rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-1 text-left hover:bg-sky-500/20 transition-colors">
        <p className="flex items-center gap-1 text-[9px] font-bold text-sky-300"><Radio className="w-3 h-3" /> Game tracker · tap to open</p>
        {mini.d && mini.d.state === 'in' && <Strip d={mini.d} />}
        {mini.d && mini.d.state === 'in' && <p className="text-[8px] text-zinc-400 truncate mt-0.5">{mini.d.situation?.text}</p>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="tracker-dialog">
          <DialogTitle className="sr-only">Game tracker</DialogTitle>
          <DialogDescription className="text-[11px] text-zinc-500" data-testid="tracker-countdown">Live · updates every {EVERY}s · next in {full.left}s</DialogDescription>
          {!full.d && !full.err && <p className="flex items-center gap-2 py-8 text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" />Loading the game…</p>}
          {full.err && <p className="text-sm text-red-400" data-testid="tracker-error">{full.err}</p>}
          {full.d && (
            <div className="space-y-4 min-w-0">
              <Header d={full.d} />
              {full.d.state === 'in' ? <Strip d={full.d} big /> : <p className="text-center text-xs text-zinc-500">{full.d.state === 'pre' ? 'Game hasn’t started yet.' : 'Final.'}</p>}
              {full.d.situation?.last_play && <p className="text-[11px] text-zinc-400" data-testid="tracker-last-play"><span className="text-zinc-500">Last play: </span>{full.d.situation.last_play}</p>}
              {plays.length > 0 && (
                <div data-testid="tracker-drive">
                  <p className="text-[11px] font-semibold text-zinc-300">{offense?.abbr} drive · {full.d.drive.description}</p>
                  <ol className="mt-1 space-y-0.5 text-[11px] text-zinc-400">
                    {[...plays].reverse().slice(0, 8).map((p, i) => <li key={i} className="border-t border-zinc-800/70 pt-0.5 break-words">{p.clock && <span className="text-zinc-500">{p.clock} </span>}{p.text}</li>)}
                  </ol>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
